import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { useLabels, usePrefs } from 'wsm-common-data';
import RichContent from './richContent/RichContent';
import useFocusTrap from '../../hooks/modern/useFocusTrap';
import VehicleCards from './VehicleCards';
import SearchLinks from './SearchLinks';
import NoResults from './NoResults';
import RecentSearches from './RecentSearches';

const DropDown = ({
	dropDownRef = React.createRef(),
	inputRef,
	inputElementRef,
	isOpen,
	recentSearches,
	dropDownPosition,
	inputWidth,
	onRemove,
	handleClearHistory,
	onReorder,
	inventoryResults,
	pageResults,
	searchTerm,
	numberOfCharactersForFirstSearchNumber,
	handleChipClick,
	handleLinkClick,
	addInfo,
	richContentToDisplay,
	triggerTrackingEvent,
	pageAlias,
	windowId,
	closeModal
}) => {
	const { suggestions = undefined, vehicles = undefined } =
		inventoryResults || {};
	const labels = useLabels();
	const prefs = usePrefs();
	const chipAriaLabel = labels.get('SEARCH');
	const removeAriaLabel = labels.get('REMOVE');
	const isSearchTermLongEnough =
		searchTerm?.length >= numberOfCharactersForFirstSearchNumber;

	const [responsiveClasses, setResponsiveClasses] = useState('col-sm-6');

	const hasResults =
		pageResults?.length > 0 ||
		suggestions?.length > 0 ||
		vehicles?.length > 0 ||
		richContentToDisplay.length > 0;

	const hasEmptyResults =
		(Array.isArray(pageResults) && pageResults?.length === 0) ||
		(Array.isArray(suggestions) && suggestions?.length === 0) ||
		(Array.isArray(vehicles) && vehicles?.length === 0);

	useFocusTrap([inputRef, dropDownRef], inputElementRef, isOpen, closeModal);

	useEffect(() => {
		setResponsiveClasses(inputWidth > 500 ? 'col-sm-6' : 'col-sm-12');
	}, [inputWidth]);

	const classNames = [
		'modern-search-dropdown',
		'box',
		'p-4',
		!isOpen && 'hidden'
	];

	const resultsWrapperClasses = [
		'flex-basis-0',
		'flex-grow-1',
		'pb-6',
		'col-mw-330'
	];

	if (
		!recentSearches?.length &&
		(!searchTerm ||
			!isSearchTermLongEnough ||
			(pageResults === undefined &&
				suggestions === undefined &&
				vehicles === undefined))
	) {
		return null;
	}

	const dropDownContent = (
		<div
			ref={dropDownRef}
			className={setClassNames(classNames)}
			data-testid="modern-input-dropdown"
			role="dialog"
			aria-labelledby={
				searchTerm ? 'results-heading' : 'recent-searches-heading'
			}
			aria-modal="true"
			style={{
				position: 'absolute',
				top: dropDownPosition.top,
				left: dropDownPosition.left,
				width: inputWidth
			}}
		>
			{isSearchTermLongEnough && !hasResults && hasEmptyResults && (
				<NoResults searchTerm={searchTerm} />
			)}

			{(!searchTerm ||
				searchTerm?.length < numberOfCharactersForFirstSearchNumber ||
				(!hasResults && !hasEmptyResults)) &&
				recentSearches?.length > 0 && (
					<RecentSearches
						recentSearches={recentSearches}
						handleClearHistory={handleClearHistory}
						chipAriaLabel={chipAriaLabel}
						removeAriaLabel={removeAriaLabel}
						onRemove={onRemove}
						onReorder={onReorder}
						handleChipClick={handleChipClick}
					/>
				)}
			{isSearchTermLongEnough && hasResults && (
				<div>
					<h2 id="results-heading" className="sr-only">
						{labels.get('SEARCH_RESULTS')}
					</h2>
					<div className="row">
						<div
							className={responsiveClasses}
							data-testid="modern-input-column-one"
						>
							<VehicleCards
								vehicles={vehicles}
								handleLinkClick={handleLinkClick}
							/>
						</div>
						<div
							className={responsiveClasses}
							data-testid="modern-input-column-two"
						>
							{richContentToDisplay.length > 0 ? (
								<div
									className={setClassNames(
										resultsWrapperClasses
									)}
								>
									<RichContent
										addInfo={addInfo}
										richContentToDisplay={
											richContentToDisplay
										}
										pageAlias={pageAlias}
										windowId={windowId}
										triggerTrackingEvent={
											triggerTrackingEvent
										}
									/>
								</div>
							) : (
								<div>
									<SearchLinks
										type="inventory"
										showResults={prefs.showInventoryResults}
										data={suggestions}
										iconClass="ddc-icon-single-vehicle"
										headingLabel={labels.get(
											'INVENTORY_SEARCH_RESULTS'
										)}
										ariaLabel={labels.get(
											'INVENTORY_SEARCH_RESULTS'
										)}
										handleLinkClick={handleLinkClick}
									/>
									<SearchLinks
										type="page"
										showResults={
											prefs.showPageSearchResults
										}
										data={pageResults}
										iconClass="ddc-icon-arrow-circle-right"
										headingLabel={labels.get(
											'PAGE_SEARCH_RESULTS'
										)}
										ariaLabel={labels.get('PAGE')}
										handleLinkClick={handleLinkClick}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
	return ReactDOM.createPortal(dropDownContent, document.body);
};
DropDown.propTypes = {
	dropDownRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.shape() })
	]),
	inputRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.shape() })
	]),
	inputElementRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.shape() })
	]),
	isOpen: PropTypes.bool.isRequired,
	recentSearches: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			id: PropTypes.string
		})
	),
	dropDownPosition: PropTypes.shape({
		top: PropTypes.number.isRequired,
		left: PropTypes.number.isRequired
	}).isRequired,
	inputWidth: PropTypes.number.isRequired,
	onRemove: PropTypes.func,
	handleClearHistory: PropTypes.func,
	onReorder: PropTypes.func,
	inventoryResults: PropTypes.shape({}),
	pageResults: PropTypes.arrayOf(PropTypes.shape({})),
	searchTerm: PropTypes.string,
	numberOfCharactersForFirstSearchNumber: PropTypes.number,
	handleChipClick: PropTypes.func,
	handleLinkClick: PropTypes.func,
	addInfo: PropTypes.shape(),
	richContentToDisplay: PropTypes.arrayOf(PropTypes.string),
	triggerTrackingEvent: PropTypes.func,
	pageAlias: PropTypes.string,
	windowId: PropTypes.string,
	closeModal: PropTypes.func
};

export default DropDown;
